import { useForm, FormProvider } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	SubmitButton,
	TextBackground,
	TextInput,
	disableEnterKey,
} from "../components";
import { BACKEND_BASE_URL } from "../constants";

/** Schema for the form on the "Contact Us" page. */
const ContactUsFormSchema = z.object({
	name: z.string(),
	email: z.string().email(),
	message: z.string(),
});

/**
 * "Contact Us" page form fields are directly based on
 * `ContactUsFormSchema`.
 */
type ContactUsFormFields = z.infer<typeof ContactUsFormSchema>;

/**
 * Define the HTML code for the Contact Us page.
 * @returns HTML code for the Contact Us page
 */
export const ContactUs = () => {
	const methods = useForm<ContactUsFormFields>({
		resolver: zodResolver(ContactUsFormSchema),
		defaultValues: {
			name: "",
			email: "",
			message: "",
		},
	});
	const { handleSubmit, reset } = methods;

	const onSubmit = async (form: ContactUsFormFields) => {
		try {
			const response = await fetch(`${BACKEND_BASE_URL}/stegg/contactus/`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify(form),
			});

			if (!response.ok) {
				throw new Error("Network response was not ok");
			}

			const responseData = await response.json();
			alert(`Form submitted successfully: ${responseData.message}`);
			reset();
		} catch (error) {
			alert(`Failed to submit task: ${error}`);
		}
	};

	return (
		<FormProvider {...methods}>
			<section className="page">
				<TextBackground id = "contact">
					<h2>Contact us!</h2>
					<div>
						We are open to ideas and suggestions, so don't hesitate
						to contact us!
						<br />
						&nbsp;
						<br />
						If you've had an issue with a particular STEGG job on
						the web server, please provide us with a link to that
						job. If you are reporting a bug, please send a detailed
						description of the error.
					</div>
				</TextBackground>
				<TextBackground>
					<form
						onSubmit={handleSubmit(onSubmit)}
						onKeyDown={(e) => disableEnterKey(e)}
					>
						<h2>Feedback</h2>
						<TextInput<ContactUsFormFields>
							name="name"
							label="Name"
						/>
						<TextInput<ContactUsFormFields>
							name="email"
							label="Email"
						/>
						<TextInput<ContactUsFormFields>
							name="message"
							isTextArea={true}
							label="Message"
						/>
						<SubmitButton<ContactUsFormFields>
							onSubmit={onSubmit}
						/>
					</form>
				</TextBackground>
			</section>
		</FormProvider>
	);
};
